import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  GraduationCap, Search, Loader2, User, Mail, Phone, Calendar,
  CheckCircle2, AlertCircle, XCircle, Clock, CreditCard, FileText,
  ChevronRight, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcademiaConsultaPage() {
  const { academiaId } = useParams();
  const [academia, setAcademia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buscando, setBuscando] = useState(false);
  const [alumno, setAlumno] = useState(null);
  const [busquedaForm, setBusquedaForm] = useState({ documento: '', email: '' });

  useEffect(() => {
    loadAcademia();
  }, [academiaId]);

  const loadAcademia = async () => {
    try {
      const res = await axios.get(`${API}/academia/${academiaId}/info-publica`);
      setAcademia(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBuscar = async (e) => {
    e.preventDefault();
    if (!busquedaForm.documento && !busquedaForm.email) {
      toast.error('Ingresa tu RUT/documento o email');
      return;
    }
    setBuscando(true);
    setAlumno(null);
    try {
      const res = await axios.post(`${API}/academia/${academiaId}/consulta-alumno`, busquedaForm);
      // La respuesta tiene estructura { alumno: {...}, academia: {...} }
      if (res.data.alumno) {
        setAlumno(res.data.alumno);
        // Actualizar academia con datos bancarios y código de pago
        setAcademia(prev => ({
          ...prev,
          datos_bancarios: res.data.academia?.datos_bancarios,
          codigo_pago: res.data.academia?.codigo_pago
        }));
      } else {
        // Respuesta antigua sin estructura (compatibilidad)
        setAlumno(res.data);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('No se encontró un alumno con esos datos');
      } else {
        toast.error(error.response?.data?.detail || 'Error al buscar');
      }
    } finally {
      setBuscando(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (!academia) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="p-8 bg-slate-800 border-slate-700 text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Academia no encontrada</h2>
          <p className="text-slate-400">El enlace que usaste no es válido o la academia ya no existe.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          {academia.logo ? (
            <img src={academia.logo} alt="" className="w-12 h-12 rounded-xl object-cover" />
          ) : (
            <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
          )}
          <div>
            <h1 className="font-bold text-white text-lg">{academia.nombre}</h1>
            <p className="text-sm text-emerald-400">Consulta de Estado</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 py-8">
        {!alumno ? (
          <Card className="p-6 bg-slate-800 border-slate-700">
            <div className="text-center mb-6">
              <Search className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-white">Consulta tu Estado</h2>
              <p className="text-slate-400 text-sm mt-1">
                Ingresa tus datos para ver tu información
              </p>
            </div>

            <form onSubmit={handleBuscar} className="space-y-4">
              <div>
                <Label className="text-slate-300">RUT o Documento</Label>
                <Input
                  value={busquedaForm.documento}
                  onChange={(e) => setBusquedaForm({ ...busquedaForm, documento: e.target.value })}
                  placeholder="12.345.678-9"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-700"></div>
                <span className="text-slate-500 text-sm">o</span>
                <div className="flex-1 h-px bg-slate-700"></div>
              </div>
              
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input
                  type="email"
                  value={busquedaForm.email}
                  onChange={(e) => setBusquedaForm({ ...busquedaForm, email: e.target.value })}
                  placeholder="tu@email.com"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={buscando}>
                {buscando ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Buscando...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Consultar
                  </>
                )}
              </Button>
            </form>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Botón volver */}
            <Button 
              variant="ghost" 
              onClick={() => { setAlumno(null); setBusquedaForm({ documento: '', email: '' }); }}
              className="text-slate-400 hover:text-white"
            >
              ← Nueva consulta
            </Button>

            {/* Info del alumno */}
            <Card className="p-6 bg-slate-800 border-slate-700">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xl font-bold">
                  {alumno.nombre?.charAt(0)}{alumno.apellido?.charAt(0)}
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-white">{alumno.nombre} {alumno.apellido}</h2>
                  {alumno.documento_numero && (
                    <p className="text-slate-400 text-sm">{alumno.documento_numero}</p>
                  )}
                </div>
                <Badge className={
                  alumno.estado === 'activo' ? 'bg-emerald-600' :
                  alumno.estado === 'suspendido' ? 'bg-amber-600' : 'bg-red-600'
                }>
                  {alumno.estado === 'activo' ? 'Activo' : 
                   alumno.estado === 'suspendido' ? 'Suspendido' : 'Inactivo'}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {alumno.email && (
                  <div className="flex items-center gap-2 text-slate-300">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <span className="text-sm">{alumno.email}</span>
                  </div>
                )}
                {alumno.telefono && (
                  <div className="flex items-center gap-2 text-slate-300">
                    <Phone className="w-4 h-4 text-slate-500" />
                    <span className="text-sm">{alumno.telefono}</span>
                  </div>
                )}
              </div>
            </Card>

            {/* Estado de Pago */}
            <Card className={`p-6 border-slate-700 ${
              alumno.estado_pago === 'al_dia' ? 'bg-emerald-900/30 border-emerald-700/50' :
              alumno.estado_pago === 'pendiente' ? 'bg-amber-900/30 border-amber-700/50' :
              'bg-red-900/30 border-red-700/50'
            }`}>
              <div className="flex items-center gap-4">
                {alumno.estado_pago === 'al_dia' ? (
                  <CheckCircle2 className="w-12 h-12 text-emerald-400" />
                ) : alumno.estado_pago === 'pendiente' ? (
                  <AlertTriangle className="w-12 h-12 text-amber-400" />
                ) : (
                  <AlertCircle className="w-12 h-12 text-red-400" />
                )}
                <div>
                  <h3 className="font-semibold text-white text-lg">
                    {alumno.estado_pago === 'al_dia' ? 'Pagos al Día' :
                     alumno.estado_pago === 'pendiente' ? 'Pago Pendiente' : 'Pago Moroso'}
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {alumno.estado_pago === 'al_dia' ? 'No tienes pagos pendientes' :
                     alumno.estado_pago === 'pendiente' ? 'Tienes un pago próximo a vencer' :
                     'Tienes pagos vencidos. Contacta a la academia.'}
                  </p>
                </div>
              </div>
            </Card>

            {/* Categorías */}
            {alumno.categorias && alumno.categorias.length > 0 && (
              <Card className="p-6 bg-slate-800 border-slate-700">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-emerald-400" />
                  Mis Categorías
                </h3>
                <div className="space-y-3">
                  {alumno.categorias.map(cat => (
                    <div key={cat.id} className="p-3 bg-slate-700/50 rounded-lg">
                      <p className="font-medium text-white">{cat.nombre}</p>
                      {cat.horarios?.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {cat.horarios.map((h, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm text-slate-400">
                              <Clock className="w-3 h-3" />
                              <span>{['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'][h.dia]} {h.hora_inicio}-{h.hora_fin}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Asistencia reciente */}
            {alumno.asistencia_reciente && alumno.asistencia_reciente.length > 0 && (
              <Card className="p-6 bg-slate-800 border-slate-700">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-emerald-400" />
                  Asistencia Reciente
                </h3>
                <div className="space-y-2">
                  {alumno.asistencia_reciente.map((a, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-slate-700/30 rounded">
                      <div>
                        <p className="text-sm text-white">{a.categoria_nombre}</p>
                        <p className="text-xs text-slate-500">{a.fecha}</p>
                      </div>
                      <Badge className={a.presente ? 'bg-emerald-600' : 'bg-red-600'}>
                        {a.presente ? 'Presente' : 'Ausente'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Datos bancarios para pago */}
            {academia.datos_bancarios?.banco && alumno.estado_pago !== 'al_dia' && (
              <Card className="p-6 bg-slate-800 border-slate-700">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-emerald-400" />
                  Datos para Transferencia
                </h3>
                <div className="bg-slate-900/50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Banco:</span>
                    <span className="text-white">{academia.datos_bancarios.banco}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Tipo:</span>
                    <span className="text-white">
                      {academia.datos_bancarios.tipo_cuenta === 'corriente' ? 'Cta. Corriente' :
                       academia.datos_bancarios.tipo_cuenta === 'vista' ? 'Cta. Vista' : 'Cta. Ahorro'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">N° Cuenta:</span>
                    <span className="text-white font-mono">{academia.datos_bancarios.numero_cuenta}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">RUT:</span>
                    <span className="text-white">{academia.datos_bancarios.rut_titular}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Nombre:</span>
                    <span className="text-white">{academia.datos_bancarios.nombre_titular}</span>
                  </div>
                </div>
                
                {/* Instrucciones con código de pago */}
                {academia.codigo_pago && (
                  <div className="mt-4 bg-emerald-900/30 border border-emerald-700/50 rounded-lg p-3">
                    <p className="text-sm text-emerald-400 font-medium mb-1">Al transferir, indica en el motivo:</p>
                    <p className="text-white font-mono text-lg">
                      {academia.codigo_pago} {alumno.rut?.replace(/\./g, '') || 'TU-RUT'}
                    </p>
                    <p className="text-xs text-slate-400 mt-2">
                      Ejemplo: {academia.codigo_pago} 12345678-9
                    </p>
                  </div>
                )}
              </Card>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-4 text-center">
        <p className="text-slate-500 text-sm">
          Powered by <span className="text-emerald-400">Kinosis</span>
        </p>
      </footer>
    </div>
  );
}
